import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { blankProblem, cleanItems, firstBlank, ITEMS_MAX, itemsProblem, labelLengthText } from "./pretrip";
import { SkeletonSection } from "./Skeleton";

interface Row {
  key: string;
  id: string | null;
  label: string;
}

const rowsOf = (list: api.PreTripList): Row[] => list.items.map((item) => ({ key: item.id, id: item.id, label: item.label }));

/** Spec F-DRV-2: the items that a driver checks before the route starts (rule D-11). */
export function PreTripSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.PreTripList | "failed" | undefined>(undefined);
  const [rows, setRows] = useState<Row[]>([]);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [blank, setBlank] = useState<string | null>(null);
  const status = useRef<HTMLParagraphElement>(null);

  const show = (next: api.PreTripList) => {
    setFound(next);
    setRows(rowsOf(next));
  };

  useEffect(() => {
    user
      .getIdToken()
      .then(api.preTrip)
      .then(show, () => setFound("failed"));
  }, [user]);

  if (found === undefined) return <SkeletonSection title="Pre-trip check" rows={3} />;
  if (found === "failed") {
    return (
      <fieldset>
        <legend>Pre-trip check</legend>
        <p className="error">Cannot load the pre-trip checklist. Try again later.</p>
      </fieldset>
    );
  }
  const change = (key: string, label: string) => {
    setRows((all) => all.map((row) => (row.key === key ? { ...row, label } : row)));
    setMessage(null);
    setBlank(null);
  };
  const remove = (key: string) => {
    setRows((all) => all.filter((row) => row.key !== key));
    setMessage(null);
    setBlank(null);
  };
  const tooLong = rows.some((row) => labelLengthText(row.label) !== null);
  const add = () => setRows((all) => [...all, { key: crypto.randomUUID(), id: null, label: "" }]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const items = cleanItems(rows);
    const empty = firstBlank(rows);
    if (empty) {
      setMessage(null);
      setBlank(empty.key);
      requestAnimationFrame(() => document.getElementById(`pre-trip-${empty.key}`)?.focus());
      return;
    }
    const problem = itemsProblem(items);
    if (problem) {
      setMessage({ ok: false, text: problem });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      show(await api.setPreTrip(await user.getIdToken(), items));
      setMessage({ ok: true, text: items.length ? `Saved. Drivers check ${items.length} items before a route.` : "Saved. Drivers start a route with the odometer only." });
      requestAnimationFrame(() => status.current?.focus());
    } catch (e) {
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The checklist was not saved. Try again." });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} noValidate data-testid="pre-trip-settings">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Pre-trip check</legend>
        <p className="hint">
          Before the first stop, the driver marks each item OK or Not OK and enters the odometer. An item that is not OK needs a note. With no items, the driver enters only
          the odometer.
        </p>
        {rows.length === 0 && <p data-testid="pre-trip-empty">No items.</p>}
        <ol className="pre-trip-items">
          {rows.map((row, index) => {
            const long = labelLengthText(row.label);
            const error = long ?? (blank === row.key ? blankProblem([row]) : null);
            return (
            <li key={row.key} data-testid="pre-trip-item">
              <label htmlFor={`pre-trip-${row.key}`} className="visually-hidden">
                Item {index + 1}
              </label>
              <input
                id={`pre-trip-${row.key}`}
                data-testid="pre-trip-label"
                value={row.label}
                aria-invalid={error !== null}
                aria-describedby={error ? `pre-trip-long-${row.key}` : undefined}
                onChange={(e) => change(row.key, e.target.value)}
              />
              {found.can_edit && (
                <button type="button" data-testid="pre-trip-remove" onClick={() => remove(row.key)} aria-label={`Remove ${row.label || `item ${index + 1}`}`}>
                  Remove
                </button>
              )}
              {error && (
                <p className="error" id={`pre-trip-long-${row.key}`} data-testid={long ? "pre-trip-long" : "pre-trip-blank"}>
                  {error}
                </p>
              )}
            </li>
            );
          })}
        </ol>
        {found.can_edit && (
          <div className="actions">
            <button type="button" data-testid="pre-trip-add" disabled={rows.length >= ITEMS_MAX} onClick={add}>
              Add an item
            </button>
            <button className="primary" data-testid="pre-trip-save" disabled={tooLong}>
              {saving ? "Saving…" : "Save the checklist"}
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="pre-trip-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </form>
  );
}
